import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '../../../ui/core';
import InputAdornment from '@mui/material/InputAdornment';
import { useFormatters } from '../../../hooks';
import { bulkUpdateManualBonus } from '../../../redux/Trips/actions';
import {
  MAX_MASS_BONUS,
  countExistingManualBonuses,
  formatBonusTripLabel,
  isAllowedBonusInput,
  parseBonusAmount,
  summarizeBulkResults,
  validateBonusAmount,
} from '../../../helpers/utils/massBonus';
import ResponsiveSheet from '../../shared/ResponsiveSheet';

// The second half of the mass-bonus flow: ops has picked the trips, this asks for
// the one amount and writes it to all of them.
//
// The amount SETS each trip's manual bonus rather than adding to it — that's what
// the endpoint does (see bulkUpdateManualBonus), and it's the same write the
// single-trip payout dialog makes — so any selected trip that already carries a
// manual bonus is called out before ops commits.
const MassBonusDialog = ({ open, trips, onClose, onApplied }) => {
  const f = useFormatters();
  const dispatch = useDispatch();

  const [value, setValue] = useState('');
  const [applying, setApplying] = useState(false);
  const [completed, setCompleted] = useState(0);
  const [summary, setSummary] = useState(null);

  // A fresh amount (and a cleared result) every time the dialog is opened, so a
  // previous run's number can't be applied to a different selection by accident.
  useEffect(() => {
    if (open) {
      setValue('');
      setApplying(false);
      setCompleted(0);
      setSummary(null);
    }
  }, [open]);

  const error = validateBonusAmount(value);
  const amount = parseBonusAmount(value);
  const replacing = countExistingManualBonuses(trips);
  const tripsById = new Map(trips.map((t) => [t.id, t]));

  const tripLabel = (tripId) => {
    const trip = tripsById.get(tripId);
    return trip ? formatBonusTripLabel(f, trip) : tripId;
  };

  const onApply = async () => {
    setApplying(true);
    setCompleted(0);
    const results = await bulkUpdateManualBonus(
      trips.map((t) => t.id),
      amount,
      (done) => setCompleted(done),
    )(dispatch);
    const nextSummary = summarizeBulkResults(results);
    setApplying(false);
    setSummary(nextSummary);
    // Always refresh, even on a partial failure: the table has to show what
    // actually landed, not what was asked for.
    onApplied && onApplied(nextSummary, amount);
    if (nextSummary.allSucceeded) {
      onClose();
    }
  };

  return (
    <ResponsiveSheet open={open} onClose={applying ? undefined : onClose}>
      <DialogTitle>Mass bonus</DialogTitle>
      <Divider />
      <DialogContent>
        {summary && !summary.allSucceeded ? (
          <Stack spacing="12px">
            <Alert severity="warning">
              {`Applied ${f.number.currency(amount)} to ${summary.appliedCount} of ${summary.attemptedCount} trips. `}
              {`${summary.failedCount} failed and ${summary.failedCount === 1 ? 'was' : 'were'} left unchanged.`}
            </Alert>
            <Typography sx={{ fontWeight: 600 }}>Not bonused</Typography>
            <Stack spacing="4px">
              {summary.failed.map((r) => (
                <Typography key={r.tripId} variant="body2" color="text.secondary">
                  {tripLabel(r.tripId)}
                </Typography>
              ))}
            </Stack>
          </Stack>
        ) : (
          <Stack spacing="16px">
            <Typography>
              {`Set the manual bonus on ${trips.length} selected ${trips.length === 1 ? 'trip' : 'trips'}.`}
            </Typography>
            {/* Two digits, whole dollars, nothing else — the amount typed here is
                written to every trip in the selection, so the field refuses input it
                can't accept instead of taking it and failing later. A `type="number"`
                input can't do that (it still accepts "100", "2.5", "1e3"), so this is
                a text field gated on isAllowedBonusInput with a numeric keypad. */}
            <TextField
              autoFocus
              label="Bonus per trip"
              value={value}
              disabled={applying}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              inputProps={{ inputMode: 'numeric', maxLength: 2, 'aria-label': 'Bonus per trip, whole dollars' }}
              onChange={(e) => {
                if (isAllowedBonusInput(e.target.value)) setValue(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !error && !applying) onApply();
              }}
              error={!!value && !!error}
              helperText={value && error ? error : `Whole dollars, up to $${MAX_MASS_BONUS}`}
            />
            {replacing > 0 && (
              <Alert severity="warning">
                {`${replacing} of these ${replacing === 1 ? 'already has' : 'already have'} a manual bonus. `}
                This replaces it — it is not added on top.
              </Alert>
            )}
            {applying && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={trips.length ? (completed / trips.length) * 100 : 0}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: '6px' }}>
                  {`Applying… ${completed} of ${trips.length}`}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {summary && !summary.allSucceeded ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button color="error" disabled={applying} onClick={onClose}>
              Cancel
            </Button>
            <Button loading={applying} disabled={!!error || !trips.length} onClick={onApply}>
              {`Apply to ${trips.length} ${trips.length === 1 ? 'trip' : 'trips'}`}
            </Button>
          </>
        )}
      </DialogActions>
    </ResponsiveSheet>
  );
};

export default MassBonusDialog;
